import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function PlatformAdmins() {
  const [hospitals, setHospitals] = useState([]);
  const [selected, setSelected] = useState("");
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/platform/hospitals");
      setHospitals(data || []);
      if (data && data[0]) setSelected(data[0].id);
    })();
  }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/platform/hospitals/${selected}/users`).then((r) => setUsers(r.data || []));
  }, [selected]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error("Select a hospital first"); return; }
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error("All fields are required"); return;
    }
    setBusy(true);
    try {
      await api.post("/platform/hospital-admins", {
        hospital_id: selected,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      toast.success("Hospital admin created");
      setForm({ name: "", email: "", password: "" });
      const { data } = await api.get(`/platform/hospitals/${selected}/users`);
      setUsers(data || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const sel = hospitals.find((h) => h.id === selected);

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>Hospital Admins</h1>
        <p className="text-sm text-slate-500">Provision an admin user for a tenant. Admins log in and manage their own staff.</p>
      </div>

      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">Assign Admin</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Hospital</Label>
              <select
                data-testid="select-hospital"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
              >
                <option value="">-- pick a hospital --</option>
                {hospitals.map((h) => (
                  <option key={h.id} value={h.id}>{h.hospital_name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Name *</Label>
              <Input data-testid="admin-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <Label>Email *</Label>
              <Input data-testid="admin-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div>
              <Label>Password *</Label>
              <Input data-testid="admin-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} />
            </div>
            <div className="md:col-span-4 flex justify-end">
              <Button type="submit" disabled={busy} data-testid="btn-create-hospital-admin">{busy ? "Saving…" : "Create Hospital Admin"}</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Users at {sel?.hospital_name || "—"}</CardTitle></CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-sm text-slate-500">No users yet for this hospital.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500 border-b">
                <tr><th className="py-2">Name</th><th>Email</th><th>Role</th></tr>
              </thead>
              <tbody data-testid="hospital-users-list">
                {users.map((u) => (
                  <tr key={u.id} className="border-b last:border-0">
                    <td className="py-2">{u.name}</td>
                    <td>{u.email}</td>
                    <td><span className="px-2 py-0.5 rounded-full bg-slate-100 text-xs">{u.role}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
